import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { captureAttribution, getHubspotCookie, type AttributionData } from '../lib/attribution';
import { countries, DEFAULT_COUNTRY, type Country } from '../lib/countries';
import { SEO } from '../components/SEO';
import { getBreadcrumbSchema } from '../lib/structured-data';
import saqrLogo from '../../assets/saqr-logo.svg';

// Inline icon components (avoiding lucide-react dependency)
const Loader2Icon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const CalendarIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
    <line x1="16" x2="16" y1="2" y2="6" />
    <line x1="8" x2="8" y1="2" y2="6" />
    <line x1="3" x2="21" y1="10" y2="10" />
  </svg>
);

// Extend Window interface for Calendly
declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement;
        prefill?: {
          name?: string;
          email?: string;
          smsReminderNumber?: string;
          customAnswers?: Record<string, string>;
        };
      }) => void;
    };
  }
}

type PageState = 'form' | 'calendly';

// Validate phone number (basic validation - at least 6 digits after dial code)
const validatePhone = (phoneString: string): boolean => {
  const digitsOnly = phoneString.replace(/\D/g, '');
  return digitsOnly.length >= 8;
};

// Load external script dynamically
const loadScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.body.appendChild(script);
  });
};

// Load external stylesheet dynamically
const loadStylesheet = (href: string): void => {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
};

/**
 * BookCallPage - Premium booking page with scattergraph animation
 */
export default function BookCallPage() {
  const { t, i18n } = useTranslation('seo');
  const isArabic = i18n.language === 'ar';
  const currentPath = isArabic ? '/ar/book-call' : '/book-call';

  // Structured data
  const structuredData = getBreadcrumbSchema([
    { name: isArabic ? 'الرئيسية' : 'Home', path: isArabic ? '/ar' : '' },
    { name: t('bookCall.title'), path: currentPath },
  ]);

  // Canvas refs for scattergraph
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // State machine
  const [pageState, setPageState] = useState<PageState>('form');

  // Form state
  const [fullName, setFullName] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Attribution
  const [attribution, setAttribution] = useState<AttributionData | null>(null);

  // Calendly
  const [calendlyScriptLoaded, setCalendlyScriptLoaded] = useState(false);
  const [calendlyInitialized, setCalendlyInitialized] = useState(false);
  const calendlyRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  // Scattergraph animation
  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = containerRef.current;
    if (!canvas || !parent) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width: number, height: number;
    let particles: { x: number; y: number; vx: number; vy: number; size: number }[] = [];
    const mouse = { x: null as number | null, y: null as number | null };
    let animationFrameId: number;

    const resize = () => {
      width = canvas.width = parent.offsetWidth;
      height = canvas.height = parent.offsetHeight;
      createParticles();
    };

    const createParticles = () => {
      particles = [];
      const count = Math.floor((width * height) / 18000);
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          size: Math.random() * 2 + 1,
        });
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };

    const handleMouseLeave = () => {
      mouse.x = null;
      mouse.y = null;
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = '#d1d5db';
        ctx.fill();

        if (mouse.x !== null && mouse.y !== null) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const connectDistance = 180;

          if (dist < connectDistance) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(14, 165, 233, ${1 - dist / connectDistance})`;
            ctx.lineWidth = 1;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();

            p.vx += dx * 0.0002;
            p.vy += dy * 0.0002;
          }
        }

        particles.forEach((p2) => {
          if (p === p2) return;
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 80) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(209, 213, 219, ${1 - dist / 80})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    parent.addEventListener('mousemove', handleMouseMove);
    parent.addEventListener('mouseleave', handleMouseLeave);

    resize();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      parent.removeEventListener('mousemove', handleMouseMove);
      parent.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Capture attribution on mount
  useEffect(() => {
    const attr = captureAttribution();
    setAttribution(attr);
  }, []);

  // Close country dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load Calendly script when needed
  useEffect(() => {
    if (pageState === 'calendly' && !calendlyScriptLoaded) {
      loadStylesheet('https://assets.calendly.com/assets/external/widget.css');
      loadScript('https://assets.calendly.com/assets/external/widget.js')
        .then(() => setCalendlyScriptLoaded(true))
        .catch(console.error);
    }
  }, [pageState, calendlyScriptLoaded]);

  // Initialize Calendly widget
  useEffect(() => {
    if (
      pageState === 'calendly' &&
      calendlyScriptLoaded &&
      !calendlyInitialized &&
      calendlyRef.current &&
      window.Calendly
    ) {
      // Format phone number: dial code + number (no spaces, remove leading 0)
      const cleanNumber = phoneNumber.replace(/^0+/, '').replace(/\s+/g, '');
      const fullPhone = `${selectedCountry.dialCode}${cleanNumber}`;

      const baseUrl = 'https://calendly.com/hello-saqracademy/discovery-call';

      // Use Calendly's prefill API - phone is Question 1 (custom question), so use customAnswers.a1
      window.Calendly.initInlineWidget({
        url: baseUrl,
        parentElement: calendlyRef.current,
        prefill: {
          name: fullName.trim(),
          email: workEmail,
          customAnswers: {
            a1: fullPhone, // Question 1 = Phone Number
          },
        },
      });
      setCalendlyInitialized(true);
    }
  }, [pageState, calendlyScriptLoaded, calendlyInitialized, fullName, workEmail, phoneNumber, selectedCountry]);

  const text = {
    headline: 'See Capability Measurement in Action',
    subheadline: 'Talk to our team about how AI assessments measure real capability change before and after your training.',
    formTitle: 'Get In Touch',
    fullNameLabel: 'Full Name',
    emailLabel: 'Work Email',
    companyLabel: 'Company Name',
    phoneLabel: 'Phone Number',
    submitButton: 'Request A Call',
    submitting: 'Processing...',
    errorRequired: 'This field is required',
    errorEmail: 'Please enter a valid email',
    errorPhone: 'Please enter a valid phone number',
    errorGeneric: 'Something went wrong. Please try again.',
    preferCall: 'Prefer to speak now?',
    callUs: 'Call us at',
    phoneNumber: '+971 52 573 2690',
    calendlyTitle: 'Select a Time',
    calendlyLoading: 'Loading scheduler...',
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setError(null);

    // Validation
    if (!fullName.trim()) {
      setError(text.errorRequired);
      return;
    }
    if (!workEmail.trim() || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(workEmail)) {
      setError(text.errorEmail);
      return;
    }
    if (!companyName.trim()) {
      setError(text.errorRequired);
      return;
    }
    const fullPhone = `${selectedCountry.dialCode} ${phoneNumber}`;
    if (!validatePhone(fullPhone)) {
      setError(text.errorPhone);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        formType: 'book_call',
        firstname: fullName.split(' ')[0] || fullName,
        lastname: fullName.split(' ').slice(1).join(' ') || '',
        email: workEmail,
        phone: fullPhone,
        company: companyName,
        team_size: '1-10', // Default for book-call leads
        utm_source: attribution?.utmSource ?? '',
        utm_medium: attribution?.utmMedium ?? '',
        utm_campaign: attribution?.utmCampaign ?? '',
        landing_page: attribution?.landingPage ?? '',
        referrer_url: attribution?.referrerUrl ?? '',
        hutk: getHubspotCookie(),
        pageUri: window.location.href,
        pageName: document.title,
      };

      // Attempt HubSpot submission but don't block on failure
      // The primary goal is to get the user to book a call
      try {
        const res = await fetch('/api/hubspot/submit-form', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          // Log but don't block - HubSpot can be fixed later
          console.warn('HubSpot submission failed, proceeding to Calendly');
        }
      } catch (hubspotError) {
        console.warn('HubSpot submission error:', hubspotError);
      }

      // Always proceed to Calendly - booking the call is the priority
      setPageState('calendly');
    } catch {
      setError(text.errorGeneric);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative min-h-screen bg-[#FAFAFA] text-slate-900 overflow-hidden"
    >
      <SEO
        title={t('bookCall.title')}
        description={t('bookCall.description')}
        path={currentPath}
        ogImageAlt={t('ogImageAlt.bookCall')}
        structuredData={structuredData}
      />
      {/* Scattergraph Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-auto"
      />

      {/* Main Content */}
      <main className="relative z-10 min-h-screen flex items-center justify-center px-6 py-10 md:py-16">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* LEFT COLUMN - Copy */}
          <div className="space-y-6 pt-8 lg:pt-4">
            {/* Logo - Above headline on desktop */}
            <div className="hidden lg:block mb-8">
              <img
                src={saqrLogo}
                alt="Saqr Academy"
                className="h-32 w-auto"
              />
            </div>
            {/* Headline - Centered on mobile, left-aligned on desktop */}
            <h1 className="font-sans text-4xl md:text-5xl lg:text-[52px] font-semibold text-slate-900 tracking-tight leading-[1.1]">
              {text.headline}
            </h1>

            {/* Subheadline - Centered on mobile, left-aligned on desktop */}
            <p className="font-sans text-lg text-slate-600 leading-relaxed max-w-xl">
              {text.subheadline}
            </p>
          </div>

          {/* RIGHT COLUMN - Form Card with 3D Flip */}
          <div
            className="w-full lg:w-[480px] h-[560px] lg:h-[620px] mx-auto"
            style={{ perspective: '1000px' }}
          >
            <div
              className="relative w-full h-full transition-transform duration-700 ease-in-out"
              style={{
                transformStyle: 'preserve-3d',
                transform: pageState === 'form' ? 'rotateY(0deg)' : 'rotateY(180deg)',
              }}
            >
              {/* Front Face - Form */}
              <div
                className="absolute inset-0 rounded-[24px] bg-white border border-slate-100 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.1)] overflow-hidden p-5 lg:p-8"
                style={{ backfaceVisibility: 'hidden' }}
              >
                <form onSubmit={handleSubmit} className="h-full flex flex-col">
                  {/* Form title - Hidden on mobile */}
                  <h2 className="hidden lg:block font-sans text-2xl font-semibold text-slate-900 mb-6">
                    {text.formTitle}
                  </h2>

                  <div className="space-y-3 lg:space-y-4 lg:flex-1">
                    {/* Full Name */}
                    <div>
                      <label className="block font-sans text-sm font-medium text-slate-600 mb-1.5">
                        {text.fullNameLabel}
                      </label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => { setFullName(e.target.value); setError(null); }}
                        placeholder="Sarah Hassan"
                        className="w-full h-12 px-4 font-sans"
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          background: '#fff',
                          color: '#0f172a',
                          fontSize: '14px',
                          boxShadow: 'none',
                          outline: 'none',
                        }}
                      />
                    </div>

                    {/* Work Email */}
                    <div>
                      <label className="block font-sans text-sm font-medium text-slate-600 mb-1.5">
                        {text.emailLabel}
                      </label>
                      <input
                        type="email"
                        value={workEmail}
                        onChange={(e) => { setWorkEmail(e.target.value); setError(null); }}
                        placeholder="sarah@acmecorp.com"
                        className="w-full h-12 px-4 font-sans"
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          background: '#fff',
                          color: '#0f172a',
                          fontSize: '14px',
                          boxShadow: 'none',
                          outline: 'none',
                        }}
                        dir="ltr"
                      />
                    </div>

                    {/* Company Name */}
                    <div>
                      <label className="block font-sans text-sm font-medium text-slate-600 mb-1.5">
                        {text.companyLabel}
                      </label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => { setCompanyName(e.target.value); setError(null); }}
                        placeholder="Acme Corp"
                        className="w-full h-12 px-4 font-sans"
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          background: '#fff',
                          color: '#0f172a',
                          fontSize: '14px',
                          boxShadow: 'none',
                          outline: 'none',
                        }}
                      />
                    </div>

                    {/* Phone Number */}
                    <div>
                      <label className="block font-sans text-sm font-medium text-slate-600 mb-1.5">
                        {text.phoneLabel}
                      </label>
                      <div className="relative" ref={countryDropdownRef}>
                        <div
                          className="flex w-full h-12 overflow-hidden"
                          style={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            background: '#fff',
                          }}
                        >
                          {/* Country Selector */}
                          <button
                            type="button"
                            onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                            className="px-3 flex items-center justify-center"
                            style={{
                              background: 'transparent',
                              border: 'none',
                              outline: 'none',
                              cursor: 'pointer',
                            }}
                          >
                            <div className="flex items-center gap-1.5">
                              <span className="text-lg leading-none">{selectedCountry.flag}</span>
                              <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </button>

                          {/* Phone Number Input */}
                          <input
                            type="tel"
                            value={phoneNumber}
                            onChange={(e) => { setPhoneNumber(e.target.value); setError(null); }}
                            placeholder="50 123 4567"
                            className="flex-1 h-full px-3 font-sans"
                            style={{
                              background: '#fff',
                              color: '#0f172a',
                              fontSize: '14px',
                              border: 'none',
                              outline: 'none',
                              boxShadow: 'none',
                            }}
                            dir="ltr"
                          />
                        </div>

                        {/* Country Dropdown */}
                        {isCountryDropdownOpen && (
                          <div
                            className="absolute left-0 top-[52px] w-full max-h-[240px] overflow-y-auto bg-white border border-slate-100 rounded-xl shadow-lg z-50 py-2"
                            style={{
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#cbd5e1 #f1f5f9',
                            }}
                          >
                            {countries.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                  setSelectedCountry(country);
                                  setIsCountryDropdownOpen(false);
                                }}
                                className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left ${
                                  selectedCountry.code === country.code ? 'bg-slate-50' : ''
                                }`}
                              >
                                <span className="text-lg leading-none">{country.flag}</span>
                                <span className="font-sans text-slate-600 text-sm font-medium min-w-[50px]">{country.dialCode}</span>
                                <span className="font-sans text-slate-500 text-sm">{country.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Error */}
                    {error && (
                      <p className="text-red-500 text-sm font-medium font-sans">{error}</p>
                    )}
                  </div>

                  {/* Submit */}
                  <div className="mt-4 lg:mt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-14 font-semibold text-white font-sans flex items-center justify-center gap-2 disabled:opacity-70"
                      style={{
                        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                        boxShadow: '0 8px 32px -8px rgba(14, 165, 233, 0.5)',
                        border: 'none',
                        borderRadius: '9999px',
                        outline: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2Icon className="w-5 h-5 animate-spin" />
                          {text.submitting}
                        </>
                      ) : (
                        <>
                          <CalendarIcon className="w-5 h-5" />
                          {text.submitButton}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              {/* Back Face - Calendly or Confirmation */}
              <div
                className="absolute inset-0 rounded-[24px] bg-white border border-slate-100 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.1)] overflow-hidden"
                style={{
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                }}
              >
                {pageState === 'calendly' && (
                  <div className="h-full flex flex-col p-6">
                    <h2 className="font-sans text-xl font-semibold text-slate-900 mb-4">
                      {text.calendlyTitle}
                    </h2>
                    <div
                      ref={calendlyRef}
                      className="flex-1 min-h-0 flex items-center justify-center rounded-lg overflow-hidden"
                    >
                      {!calendlyInitialized && (
                        <span className="font-sans text-slate-500 text-sm">{text.calendlyLoading}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Prefer to speak now - Under form card */}
            <div className="text-center font-sans text-[11px] lg:text-sm text-slate-400 mt-4">
              <span className="block lg:inline">{text.preferCall}</span>
              <span className="hidden lg:inline">{' '}</span>
              <a
                href={`tel:${text.phoneNumber.replace(/\s/g, '')}`}
                className="block lg:inline text-slate-400 hover:text-green-600 transition-colors underline decoration-dashed decoration-slate-300 underline-offset-2"
              >
                {text.callUs} {text.phoneNumber}
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
